<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'

interface SelectOption {
  value: string | number
  label: string
}

interface Props {
  modelValue: string | number | null
  options: SelectOption[]
  placeholder: string
  disabled?: boolean
  searchable?: boolean
  isOpen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  searchable: false,
  isOpen: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  search: [query: string]
  'update:isOpen': [value: boolean]
}>()

const isOpen = computed({
  get: () => props.isOpen,
  set: (value: boolean) => emit('update:isOpen', value),
})
const searchQuery = ref('')
const dropdownRef = ref<HTMLDivElement | null>(null)
const maxHeight = ref(480)
const expandDirection = ref<'down' | 'up'>('down')

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.options
  }

  try {
    const regex = new RegExp(searchQuery.value, 'i')
    return props.options.filter(opt => regex.test(opt.label))
  } catch {
    return props.options
  }
})

const isRegexValid = computed(() => {
  if (!searchQuery.value) return true
  try {
    new RegExp(searchQuery.value, 'i')
    return true
  } catch {
    return false
  }
})

const selectedLabel = computed(() => {
  const selected = props.options.find(opt => opt.value === props.modelValue)
  return selected ? selected.label : props.placeholder
})

const calculateMaxHeight = async () => {
  await nextTick()
  if (!dropdownRef.value) return

  const dropdown = dropdownRef.value
  const rect = dropdown.getBoundingClientRect()

  // 找最近的 Frame 容器
  let frameElement = dropdown.closest('.frame') as HTMLElement | null
  let maxHeightValue = 480

  if (frameElement) {
    // 相對於 Frame 容器計算
    const frameRect = frameElement.getBoundingClientRect()
    const frameBottom = frameRect.bottom

    // 下拉菜單打開位置（按鈕頂部 + 按鈕高度）
    const dropdownTop = rect.top + 32

    // 計算到 Frame 底部的距離
    const spaceBelow = frameBottom - dropdownTop - 20 // 預留 20px 邊距

    if (spaceBelow > 200) {
      // 下方有足夠空間：使用下方空間
      maxHeightValue = Math.max(240, Math.min(480, spaceBelow))
      expandDirection.value = 'down'
    } else {
      // 下方空間不足：改為向上展開
      const spaceAbove = rect.top - frameRect.top - 20
      maxHeightValue = Math.max(240, Math.min(480, spaceAbove))
      expandDirection.value = 'up'
    }
  } else {
    // fallback：相對於視窗計算
    const windowHeight = window.innerHeight
    const spaceBelow = windowHeight - rect.top - 32

    if (spaceBelow > 200) {
      maxHeightValue = Math.max(240, Math.min(480, spaceBelow - 20))
      expandDirection.value = 'down'
    } else {
      const spaceAbove = rect.top - 40
      maxHeightValue = Math.max(240, Math.min(480, spaceAbove))
      expandDirection.value = 'up'
    }
  }

  maxHeight.value = maxHeightValue
}

const handleToggle = () => {
  const newState = !props.isOpen
  isOpen.value = newState
  if (newState) {
    calculateMaxHeight()
  }
}

const handleSelect = (value: string | number) => {
  emit('update:modelValue', value)
  emit('search', '')
  isOpen.value = false
  searchQuery.value = ''
}

const handleSearchInput = (e: Event) => {
  const input = e.target as HTMLInputElement
  searchQuery.value = input.value
  emit('search', searchQuery.value)
}
</script>

<template>
  <div class="cascade-select" ref="dropdownRef">
    <button
      class="cascade-select-trigger"
      :disabled="disabled"
      @click="handleToggle"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
    >
      <span class="truncate">{{ selectedLabel }}</span>
      <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': isOpen }" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    </button>

    <div v-if="isOpen && !disabled" class="cascade-select-dropdown" :class="{ 'cascade-select-dropdown-up': expandDirection === 'up' }">
      <div v-if="searchable" class="cascade-select-search">
        <input
          type="text"
          :value="searchQuery"
          @input="handleSearchInput"
          placeholder="搜尋..."
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
          :class="{ 'border-red-300': !isRegexValid }"
        />
        <div v-if="!isRegexValid" class="text-xs text-red-500 mt-1">無效的搜尋表達式</div>
      </div>

      <div class="cascade-select-list" :style="{ maxHeight: `${maxHeight - (searchable ? 56 : 0)}px` }">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="cascade-select-item"
          @click="handleSelect(option.value)"
        >
          {{ option.label }}
        </div>
        <div v-if="filteredOptions.length === 0" class="cascade-select-empty">
          無符合項目
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cascade-select {
  position: relative;
  width: 100%;
}

.cascade-select-trigger {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  color: #1f2937;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  white-space: nowrap;
  overflow: hidden;
  transition: all 0.2s;
  height: var(--btn-height);
}

.cascade-select-trigger:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.cascade-select-trigger:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.cascade-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-width: 300px;
}

.cascade-select-dropdown-up {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 4px;
}

.cascade-select-search {
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.cascade-select-list {
  overflow-y: auto;
  overflow-x: hidden;
}

.cascade-select-item {
  padding: 8px 12px;
  color: #1f2937;
  font-size: var(--font-size-base);
  cursor: pointer;
  transition: background-color 0.15s;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cascade-select-item:hover {
  background-color: #f3f4f6;
}

.cascade-select-empty {
  padding: 12px;
  color: #6b7280;
  font-size: var(--font-size-base);
  text-align: center;
}
</style>
